import { NavigatorConfiguration, NavigatorLayoutState } from './types';
import { combineLatest, from, map, Observable, of } from 'rxjs';
import { getEventListeners } from './navigatorEventListeners';
import { getNavigatorClickRoutine } from './routines/navigatorClickRoutine';
import { stateToRenderProps } from './streams/stateToRenderProps';
import { NavigatorContainerProps } from './components/PropTypes';
import { getNavigatorStream } from './streams/navigator$';
import { withOnFirstSubscription } from '@ardoq/common-helpers';
import { getHandleSelectionEnd } from './routines/componentSelectionRoutines';
import { action$, collectRoutines, subscribeRoutine } from '@ardoq/rxbeach';
import type { TreeOptions } from './models/tree';

export type SubscribeToNavigatorStreamAndRoutinesArgs = {
  setState: React.Dispatch<React.SetStateAction<NavigatorContainerProps>>;
  navigatorConfiguration: NavigatorConfiguration;
};

export const toTreeOptions = ({
  eventHandlers,
  isAsyncSearch,
}: NavigatorConfiguration): TreeOptions => ({
  hasCreateComponent: Boolean(eventHandlers.onCreateNewComponent),
  isAsyncSearch: Boolean(isAsyncSearch),
});

export const subscribeToNavigatorStreamAndRoutines = ({
  setState,
  navigatorConfiguration,
}: SubscribeToNavigatorStreamAndRoutinesArgs) => {
  const navigator$ = getNavigatorStream({
    externalReducers: navigatorConfiguration.externalReducers,
    namespace: navigatorConfiguration.actionNamespace,
    treeOptions: toTreeOptions(navigatorConfiguration),
  });

  const navigatorContainer$ = getNavigatorContainerStream({
    navigator$,
    navigatorConfiguration$: from([navigatorConfiguration]),
  });
  const navigatorClickRoutine = getNavigatorClickRoutine(
    navigator$,
    navigatorConfiguration
  );
  const { componentSelection$, rangeSelectionMode, actionNamespace } =
    navigatorConfiguration;
  const handleSelectionEndRoutine =
    componentSelection$ && rangeSelectionMode !== 'noRangeSelection'
      ? getHandleSelectionEnd({
          navigator$,
          componentSelection$,
          rangeSelectionMode,
          actionNamespace,
        })
      : of(null);
  const additionalRoutines = (
    navigatorConfiguration.additionalRoutines ?? []
  ).map(getRoutine =>
    getRoutine({ navigator$, navigatorContainer$, navigatorConfiguration })
  );
  const routine = collectRoutines(
    navigatorClickRoutine,
    handleSelectionEndRoutine,
    ...additionalRoutines
  );
  const subscriptions = [
    withOnFirstSubscription(
      navigatorContainer$,
      navigatorConfiguration.onViewInitialized ?? (() => {})
    ).subscribe(setState),
    subscribeRoutine(action$, routine),
  ];

  return () => {
    subscriptions.forEach(subscription => subscription.unsubscribe());
    if (navigatorConfiguration.onViewDestroyed) {
      navigatorConfiguration.onViewDestroyed();
    }
  };
};

type MapToNavigatorContainerPropsArgs = {
  navigatorState: NavigatorLayoutState;
  navigatorConfiguration: NavigatorConfiguration;
};

export const mapToNavigatorContainerProps = ({
  navigatorState,
  navigatorConfiguration,
}: MapToNavigatorContainerPropsArgs): NavigatorContainerProps =>
  stateToRenderProps({
    ...navigatorState,
    navigatorConfiguration,
    eventListeners: getEventListeners(navigatorConfiguration),
    container: navigatorState.container ?? document.createElement('div'),
    isInDiffMode: navigatorState.tree.navigatorViewInterface.isInDiffMode(),
    isScenarioMode:
      navigatorState.tree.navigatorViewInterface.isInScenarioMode(),
    activeDiffMode: navigatorState.tree.navigatorViewInterface.getDiffMode(),
  });

export const getNavigatorContainerStream = ({
  navigator$,
  navigatorConfiguration$,
}: {
  navigator$: Observable<NavigatorLayoutState>;
  navigatorConfiguration$: Observable<NavigatorConfiguration>;
}): Observable<NavigatorContainerProps> =>
  combineLatest({
    navigatorState: navigator$,
    navigatorConfiguration: navigatorConfiguration$,
  }).pipe(map(mapToNavigatorContainerProps));
